import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types
export interface Occupation {
	_id: string;
	name: string;
	category_slug: string;
	base_fee: string;
	image?: string;
	icon?: string;
	totalVendors: number;
	male: number;
	female: number;
}

export interface Category {
	_id: string;
	name: string;
	slug: string;
}

export interface Bank {
	_id: string;
	name: string;
	code: string;
}

export interface IntervalFeeType {
	_id?: string;
	start_time: string;
	end_time: string;
	percent: string;
}

export interface VariableFee {
	_id: string;
	amount_per_km: number;
	time_variation: IntervalFeeType[];
}

// State interface
export interface MiscState {
	// Data
	occupations: Occupation[];
	categories: Category[];
	banks: Bank[];
	variableFee: VariableFee | null;

	// Loading states
	loadingOccupations: boolean;
	loadingCategories: boolean;
	loadingBanks: boolean;
	loadingVariableFee: boolean;

	// Operation loading states
	addingOccupation: boolean;
	editingOccupation: boolean;
	deletingOccupation: boolean;
	addingCategory: boolean;
	editingCategory: boolean;
	deletingCategory: boolean;
	settingVariableFee: boolean;
	deletingCache: boolean;

	// Filters and search
	searchField: string;
	activeTab: string;

	// Pagination
	currentPage: number;
	itemsPerPage: number;

	// UI states
	showAddOccupationModal: boolean;
	showEditOccupationModal: boolean;
	showAddCategoryModal: boolean;
	showEditCategoryModal: boolean;
	selectedOccupation: Occupation | null;
	selectedCategory: Category | null;

	// Error states
	error: string | null;
	occupationError: string | null;
	categoryError: string | null;
	bankError: string | null;
	variableFeeError: string | null;
}

// Initial state
const initialState: MiscState = {
	// Data
	occupations: [],
	categories: [],
	banks: [],
	variableFee: null,

	// Loading states
	loadingOccupations: false,
	loadingCategories: false,
	loadingBanks: false,
	loadingVariableFee: false,

	// Operation loading states
	addingOccupation: false,
	editingOccupation: false,
	deletingOccupation: false,
	addingCategory: false,
	editingCategory: false,
	deletingCategory: false,
	settingVariableFee: false,
	deletingCache: false,

	// Filters and search
	searchField: "",
	activeTab: "All",

	// Pagination
	currentPage: 1,
	itemsPerPage: 10,

	// UI states
	showAddOccupationModal: false,
	showEditOccupationModal: false,
	showAddCategoryModal: false,
	showEditCategoryModal: false,
	selectedOccupation: null,
	selectedCategory: null,

	// Error states
	error: null,
	occupationError: null,
	categoryError: null,
	bankError: null,
	variableFeeError: null,
};

// Slice
const miscSlice = createSlice({
	name: "misc",
	initialState,
	reducers: {
		// Data setters
		setOccupations: (state, action: PayloadAction<Occupation[]>) => {
			state.occupations = action.payload;
		},
		setCategories: (state, action: PayloadAction<Category[]>) => {
			state.categories = action.payload;
		},
		setBanks: (state, action: PayloadAction<Bank[]>) => {
			state.banks = action.payload;
		},
		setVariableFee: (state, action: PayloadAction<VariableFee>) => {
			state.variableFee = action.payload;
		},

		// Loading state setters
		setLoadingOccupations: (state, action: PayloadAction<boolean>) => {
			state.loadingOccupations = action.payload;
		},
		setLoadingCategories: (state, action: PayloadAction<boolean>) => {
			state.loadingCategories = action.payload;
		},
		setLoadingBanks: (state, action: PayloadAction<boolean>) => {
			state.loadingBanks = action.payload;
		},
		setLoadingVariableFee: (state, action: PayloadAction<boolean>) => {
			state.loadingVariableFee = action.payload;
		},

		// Operation loading state setters
		setAddingOccupation: (state, action: PayloadAction<boolean>) => {
			state.addingOccupation = action.payload;
		},
		setEditingOccupation: (state, action: PayloadAction<boolean>) => {
			state.editingOccupation = action.payload;
		},
		setDeletingOccupation: (state, action: PayloadAction<boolean>) => {
			state.deletingOccupation = action.payload;
		},
		setAddingCategory: (state, action: PayloadAction<boolean>) => {
			state.addingCategory = action.payload;
		},
		setEditingCategory: (state, action: PayloadAction<boolean>) => {
			state.editingCategory = action.payload;
		},
		setDeletingCategory: (state, action: PayloadAction<boolean>) => {
			state.deletingCategory = action.payload;
		},
		setSettingVariableFee: (state, action: PayloadAction<boolean>) => {
			state.settingVariableFee = action.payload;
		},
		setDeletingCache: (state, action: PayloadAction<boolean>) => {
			state.deletingCache = action.payload;
		},

		// Filter and search setters
		setSearchField: (state, action: PayloadAction<string>) => {
			state.searchField = action.payload;
		},
		setActiveTab: (state, action: PayloadAction<string>) => {
			state.activeTab = action.payload;
		},

		// Pagination setters
		setCurrentPage: (state, action: PayloadAction<number>) => {
			state.currentPage = action.payload;
		},
		setItemsPerPage: (state, action: PayloadAction<number>) => {
			state.itemsPerPage = action.payload;
		},

		// UI state setters
		setShowAddOccupationModal: (state, action: PayloadAction<boolean>) => {
			state.showAddOccupationModal = action.payload;
		},
		setShowEditOccupationModal: (state, action: PayloadAction<boolean>) => {
			state.showEditOccupationModal = action.payload;
		},
		setShowAddCategoryModal: (state, action: PayloadAction<boolean>) => {
			state.showAddCategoryModal = action.payload;
		},
		setShowEditCategoryModal: (state, action: PayloadAction<boolean>) => {
			state.showEditCategoryModal = action.payload;
		},
		setSelectedOccupation: (
			state,
			action: PayloadAction<Occupation | null>
		) => {
			state.selectedOccupation = action.payload;
		},
		setSelectedCategory: (state, action: PayloadAction<Category | null>) => {
			state.selectedCategory = action.payload;
		},

		// Error state setters
		setError: (state, action: PayloadAction<string | null>) => {
			state.error = action.payload;
		},
		setOccupationError: (state, action: PayloadAction<string | null>) => {
			state.occupationError = action.payload;
		},
		setCategoryError: (state, action: PayloadAction<string | null>) => {
			state.categoryError = action.payload;
		},
		setBankError: (state, action: PayloadAction<string | null>) => {
			state.bankError = action.payload;
		},
		setVariableFeeError: (state, action: PayloadAction<string | null>) => {
			state.variableFeeError = action.payload;
		},

		// Reset actions
		resetMiscState: () => initialState,
		resetErrors: (state) => {
			state.error = null;
			state.occupationError = null;
			state.categoryError = null;
			state.bankError = null;
			state.variableFeeError = null;
		},
	},
});

// Export actions
export const {
	setOccupations,
	setCategories,
	setBanks,
	setVariableFee,
	setLoadingOccupations,
	setLoadingCategories,
	setLoadingBanks,
	setLoadingVariableFee,
	setAddingOccupation,
	setEditingOccupation,
	setDeletingOccupation,
	setAddingCategory,
	setEditingCategory,
	setDeletingCategory,
	setSettingVariableFee,
	setDeletingCache,
	setSearchField,
	setActiveTab,
	setCurrentPage,
	setItemsPerPage,
	setShowAddOccupationModal,
	setShowEditOccupationModal,
	setShowAddCategoryModal,
	setShowEditCategoryModal,
	setSelectedOccupation,
	setSelectedCategory,
	setError,
	setOccupationError,
	setCategoryError,
	setBankError,
	setVariableFeeError,
	resetMiscState,
	resetErrors,
} = miscSlice.actions;

// Export reducer
export default miscSlice.reducer;
